import {
  patchState,
  signalStore,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { BaseState, withCrudOperations } from '../crud.state';
import { Category } from '../../models/category';
import { withCategorySelectors } from './category.selectors';
import { CategoryService } from '../../services/category.service';
import { IndicatorTreeNode } from '../../models/IndicatorTreeNode';
import { inject } from '@angular/core';
import { SocketService } from '../../services/socket.service';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { IndicatorStore } from '../indicator/indicator.state';
import { ActiveUserStore } from '../user/active-user.state';
import { mergeArrays } from '../../utility/array.utility';
import { withLogging } from '../features/logger.feature';
import { RoleBasedHandler } from '../../handlers/role-based-handler';
import { withWorkspaceHelpers } from '../workspace-helper.selectors';

export type CategoryState = BaseState<Category>;

export const initialState: CategoryState = {
  items: {},
  loading: false,
  selectedId: '',
  selection: [],
  initialized: false,
  itemsLoaded: false,
  version: 0,
  lastPersistedId: '',
  trigger: undefined,
  entityType: 'IndicatorCategory',
  entityLabel: 'Ordner',
  filter: {
    query: '',
    category: [],
    tags: undefined,
    products: undefined,
    published: null,
    locked: null,
    ids: undefined,
  },
  events: {
    add: ['rebuildTree'],
    update: ['rebuildTree'],
    delete: ['rebuildTree', 'removeIndicators'],
    socket: ['rebuildTree'],
  },
};

export const CategoryStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withCrudOperations<Category>(CategoryService),
  withLogging(initialState.entityType),
  withCategorySelectors(),
  withWorkspaceHelpers(),
  withMethods(
    (
      store,
      categoryService = inject(CategoryService),
      socketService = inject(SocketService)
    ) => ({
      updateLockStatus: rxMethod<{ ids: Array<string>; isLocked: boolean }>(
        pipe(
          switchMap(({ ids, isLocked }) => {
            return categoryService.updateLockStatus(ids, isLocked).pipe(
              tapResponse({
                next: response => {
                  const updatedItems = { ...store.items() };
                  ids.forEach(id => {
                    updatedItems[id].locked = isLocked;
                  });

                  patchState(store, {
                    items: updatedItems,
                    trigger: store.events ? store.events().update : undefined,
                  });
                  setTimeout(
                    () => patchState(store, { trigger: undefined }),
                    0
                  );

                  // emit socket event after update item
                  socketService.emitEvent('baseEvent', {
                    action: 'persistMultiple',
                    commune: 'dev',
                    body: updatedItems,
                    type: 'category',
                    trigger: store.events ? store.events().socket : undefined,
                  });
                },
                error: console.error,
                //finalize: () => patchState(store, { loading: false }),
              })
            );
          })
        )
      ),

      /**
       * returns one store-item by id
       * @param id
       */
      getByIdAsTreeItem(id: string): IndicatorTreeNode {
        const category = store.items()[id];

        return {
          _id: category._id || '',
          key: category._id || '',
          label: category.name,
          locked: category.locked,
          type: 'category',
          children: [],
          sorting: category.sorting,
        };
      },

      /**
       * returns category-parent-path as string starting with category by id
       * @param id
       */
      getPathById(id: string): string {
        let category = store.items()[id];
        if (!category) return '';
        const path: string[] = [category.name];

        const parentGuard: string[] = []; // if parents are miss-linked to itself or children
        while (category.parent && !parentGuard.includes(category.parent)) {
          category = store.items()[category.parent];
          if (!category) return '';
          parentGuard.push(category._id);
          path.unshift(category.name);
        }

        return path.join('/ ') as string;
      },

      /**
       * checks for locked parents
       * @param id
       */
      hasLockedParents(id: string): boolean {
        let category = store.items()[id];

        while (category.parent) {
          if (category.locked) return true;
          category = store.items()[category.parent];
        }

        return false;
      },

      /**
       * get all workspaces from one category recursively from parents
       * @param id
       */
      getAllWorkspacesFromPath(id: string): string[] {
        let category = store.items()[id];
        if (!category) return [];
        let workspaces: string[] = category.workspaces || [];
        while (category && category.parent) {
          category = store.items()[category.parent];
          if (category && category.workspaces) {
            workspaces = mergeArrays<string>(workspaces, category.workspaces);
          }
        }
        return workspaces;
      },

      /**
       * get all workspaces from one category recursively from parents
       * @param parentId
       * @param categories
       */
      getAllChildren(parentId: string, categories: Category[]): Category[] {
        // Get all categories where parent === parentId
        const children = categories.filter(
          category => category.parent === parentId
        );

        // Base case: If no children, return an empty array
        if (children.length === 0) {
          return [];
        }

        // Recursively get children of each child category
        return children.reduce((allChildren, child) => {
          return [
            ...allChildren,
            child,
            ...this.getAllChildren(child._id, categories),
          ];
        }, [] as Category[]);
      },

      /**
       * Method to fetch all child categories for a given parent ID.
       * @param parentId - The ID of the parent category.
       * @returns An array of child categories.
       */
      fetchCategoryChildren(parentId: string): Category[] {
        const categories = store.items();
        return this.getAllChildren(parentId, Object.values(categories));
      },
      getParents(id: string): Category[] {
        const parents: Category[] = [];
        const items = store.items();
        let currentCategory: Category = store.getById(id);
        while (currentCategory.parent) {
          const parentCategory = items[currentCategory.parent];
          if (parentCategory) {
            parents.push(parentCategory);
          }
          currentCategory = parentCategory;
        }
        return parents;
      },
      addParentsToSelection(): void {
        const selection = store.selection();
        const items = store.items();
        const newSelection = [...selection];
        selection.forEach(category => {
          let currentCategory = category;
          while (currentCategory.parent) {
            const parentCategory = items[currentCategory.parent];
            if (
              parentCategory &&
              !newSelection.find(c => c._id === parentCategory._id)
            ) {
              newSelection.push(parentCategory);
            }
            currentCategory = parentCategory;
          }
        });
        patchState(store, { selection: newSelection });
      },
      addChildrenToSelection(): void {
        const selection: Category[] = store.selection();
        const allChildrenSet = new Set<string>(selection.map(c => c._id));
        for (const category of selection) {
          const children = this.fetchCategoryChildren(category._id);
          children.forEach(child => allChildrenSet.add(child._id));
        }

        const items = store.items();

        // Map back the IDs to full Category objects
        const newSelection = Array.from(allChildrenSet).map(id => items[id]);

        patchState(store, { selection: newSelection });
      },
    })
  ),
  withHooks({
    onInit(store) {
      store.loadAllItemsByPromise();
    },
    onDestroy() {
      console.log('on destroy');
    },
  })
);
